<script setup lang="ts">
    import { computed, ref, watch, type Ref } from 'vue'
    import { DatePicker, type DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs'

    import { insertIntoTextArea1, localToUtcDate } from "@common/Common"
    import { ValidationError } from '@common/types'

    import
        {
            AttributeEditorDialogViewModel,
            SaveDateAttributeEventPayload,
            SaveEmailAttributeEventPayload,
            SaveMultilineAttributeEventPayload,
            SaveNumericAttributeEventPayload,
            SaveTextAttributeEventPayload,
            TestScriptEventPayload
        } from '@Quest/CorporateAttributes/Components/AttributeEditorDialog'

    import type { AttributeDefinedValueReadModel } from '@Alternatives/Attributes/Domain/readmodels'
    import { RealDefinedValue } from '@Alternatives/Model'

    const props = defineProps<{
        viewModel: AttributeEditorDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'test-script', payload: TestScriptEventPayload): void
        (e: 'save-numeric', payload: SaveNumericAttributeEventPayload): void
        (e: 'save-date', payload: SaveDateAttributeEventPayload): void
        (e: 'save-text', payload: SaveTextAttributeEventPayload): void
        (e: 'save-email', payload: SaveEmailAttributeEventPayload): void
        (e: 'save-multiline', payload: SaveMultilineAttributeEventPayload): void
    }>()

    const vm = props.viewModel

    const opened = computed(() => vm?.opened ?? false)
    const initialized = computed(() => vm.initialized)

    const dialogTitle = computed(() => vm.dialogTitle)
    const confirmButtonLabel = computed(() => vm.confirmButtonLabel)
    const allowChangeAttributeType = computed(() => vm.allowChangeAttributeType)
    const allowMultipleValuesEnabled = computed(() => vm.allowMultipleValuesEnabled)
    const allowInputTypeEvaluate = computed(() => vm.allowInputTypeEvaluate)

    const scriptTestInputs = computed(() => vm.scriptTestInputs)
    const scales = computed(() => vm.scales)
    const numericAttributeNames = computed(() => vm.numericAttributeNames)

    const nameError = computed(() => vm.errors.find(e => e.Context === "Name")?.Error ?? null)
    const descriptionError = computed(() => vm.errors.find(e => e.Context === "Description")?.Error ?? null)
    const sectionError = computed(() => vm.errors.find(e => e.Context === "Section")?.Error ?? null)
    const scriptError = computed(() => vm.errors.find(e => e.Context === "Script")?.Error ?? null)
    const definedValuesErrors = computed(() => vm.errors.filter(e => e.Context === "DefinedValues").map(e => e.Error))

    const id = computed(() => vm.attributeId)

    const name = computed({
        get: () => vm.name,
        set: v => vm.name = v
    })

    const section = computed({
        get: () => vm.section,
        set: v => vm.section = v
    })

    const description = computed({
        get: () => vm.description,
        set: v => vm.description = v
    })

    const script = computed({
        get: () => vm.script,
        set: v => vm.script = v
    })

    const attributeType = computed({
        get: () => vm.attributeType,
        set: v => vm.attributeType = v
    })

    const inputType = computed({
        get: () => vm.inputType,
        set: v => vm.inputType = v
    })

    const isMultivalue = computed({
        get: () => vm.isMultivalue,
        set: v => vm.isMultivalue = v
    })

    const minLength = computed({
        get: () => vm.minLength,
        set: v => vm.minLength = v
    })

    const maxLength = computed({
        get: () => vm.maxLength,
        set: v => vm.maxLength = v
    })

    const minValue = computed({
        get: () => vm.minValue,
        set: v => vm.minValue = v
    })

    const maxValue = computed({
        get: () => vm.maxValue,
        set: v => vm.maxValue = v
    })

    const minDate = computed({
        get: () => vm.minDate,
        set: v => vm.minDate = v
    })

    const maxDate = computed({
        get: () => vm.maxDate,
        set: v => vm.maxDate = v
    })

    const rows = computed({
        get: () => vm.rows,
        set: v => vm.rows = v
    })

    const definedValues = computed({
        get: () => vm.definedValues,
        set: v => vm.definedValues = v
    })

    const scriptTestResult = computed({
        get: () => vm.scriptTestResult,
        set: v => vm.scriptTestResult = v
    })

    const testScriptPanelVisible = ref(false)
    const ta = ref<HTMLTextAreaElement | null>(null)


    watch(testScriptPanelVisible, (val) =>
    {
        if (val === true)
        {
            const re = /\[([^\]]*)\]/g
            const matches: Set<string> = new Set()

            let match
            while ((match = re.exec(script.value ?? "")) !== null)
            {
                matches.add(match[1])
            }

            const newValues = Array.from(matches).map(a =>
            {
                const vals = scriptTestInputs.value.filter(b => b.Name === a)
                return { Name: a, Value: vals.length > 0 ? vals[0].Value : 0 }
            })

            vm.scriptTestInputs = newValues
        }
    })

    function addDefinedValue()
    {
        definedValues.value.push({ Name: "", Value: null, Description: null })
    }

    function removeDefinedValue(dv: AttributeDefinedValueReadModel)
    {
        definedValues.value = definedValues.value.filter(x => x !== dv)
    }

    function insertAttributeIntoScript(attrName: string)
    {
        if (!ta.value) return
        insertIntoTextArea1(ta.value, `[${attrName}]`)
        ta.value.dispatchEvent(new Event('input'))
    }

    function onMinDateChanged(event: DatePickerChangeEvent)
    {
        const localDate = event.target.value as Date
        minDate.value = localToUtcDate(localDate)
    }

    function onMaxDateChanged(event: DatePickerChangeEvent)
    {
        const localDate = event.target.value as Date
        maxDate.value = localToUtcDate(localDate)
    }

    function onDefinedLevelDateChanged(event: DatePickerChangeEvent, dv: AttributeDefinedValueReadModel)
    {
        const localDate = event.target.value as Date
        dv.Value = localToUtcDate(localDate)
    }

    function handleLoadScriptTestResult()
    {
        scriptTestResult.value = null
        emit('test-script', new TestScriptEventPayload(script.value, scriptTestInputs.value))
    }

    function handleFormSubmit()
    {
        vm.clearValidationErrors()

        const filteredDefinedValues =
            definedValues.value.filter(a => a.Name || a.Value || a.Description)

        switch (attributeType.value)
        {
            case "Real":
                emit('save-numeric',
                    new SaveNumericAttributeEventPayload(id.value, name.value, description.value, section.value, inputType.value,
                        isMultivalue.value, minValue.value, maxValue.value, script.value, filteredDefinedValues as RealDefinedValue[]))
                break

            case "Date":
                emit('save-date',
                    new SaveDateAttributeEventPayload(id.value, name.value, description.value, section.value, inputType.value, isMultivalue.value, minDate.value, maxDate.value, filteredDefinedValues))
                break

            case "Text":
                emit('save-text',
                    new SaveTextAttributeEventPayload(id.value, name.value, description.value, section.value, inputType.value, isMultivalue.value, minLength.value, maxLength.value, filteredDefinedValues))
                break

            case "Email":
                emit('save-email',
                    new SaveEmailAttributeEventPayload(id.value, name.value, description.value, section.value, inputType.value, isMultivalue.value, filteredDefinedValues))
                break

            case "MultilineText":
                emit('save-multiline',
                    new SaveMultilineAttributeEventPayload(id.value, name.value, description.value, section.value, rows.value ?? 3, minLength.value, maxLength.value))
                break

            default:
                console.log(`Unknown attribute type: ${attributeType.value}`)
                break
        }
    }

    function close()
    {
        vm.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{dialogTitle}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="initialized" class="container-fluid">
                            <form id="attribute-editor-dialog-form" @submit.prevent="handleFormSubmit">
                                <div class="row">
                                    <div class="col-sm-6">
                                        <div class="form-group">
                                            <label><span class="text-danger">*</span> Name</label>
                                            <input v-model.trim="name" type="text" class="form-control" required minlength="2" maxlength="100" />
                                            <small>Name of the attribute</small>
                                            <div class="error">{{nameError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-sm-6">
                                        <div class="form-group">
                                            <label>Section</label>
                                            <input type="text" v-model.trim="section" class="form-control" maxlength="100" />
                                            <small>Name of the section. Use sections to group attributes e.g. financials.</small>
                                            <div class="error">{{sectionError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-sm-12">
                                        <div class="form-group">
                                            <label>Description</label>
                                            <textarea v-model.trim="description" type="text" class="form-control" rows="3" maxlength="500"></textarea>
                                            <small>Description of the attribute.</small>
                                            <div class="error">{{descriptionError}}</div>
                                        </div>
                                    </div>
                                </div>
                                <hr />

                                <div class="row">
                                    <div class="col-sm-4">
                                        <div class="form-group">
                                            <label>Type</label>
                                            <select class="form-control" v-model="attributeType" :disabled="!allowChangeAttributeType" required>
                                                <option value="Real">Numeric</option>
                                                <option value="Text">Text</option>
                                                <option value="MultilineText">Multiline text</option>
                                                <option value="Date">Date</option>
                                                <option value="Email">Email</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div class="col-sm-4" v-if="attributeType === 'Real'">
                                        <div class="form-group">
                                            <label>Input type</label>
                                            <select class="form-control" v-model="inputType" required>
                                                <option value="Manual">Enter value manually</option>
                                                <option value="List">Pick value from list</option>
                                                <option value="Evaluate" :disabled="allowInputTypeEvaluate === false">
                                                    Based on formula
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <template v-if="attributeType !== 'MultilineText'">
                                        <div class="col-sm-4" v-if="attributeType !== 'Real'">
                                            <div class="form-group">
                                                <label>Input type</label>
                                                <select class="form-control" v-model="inputType">
                                                    <option value="Manual">Enter value manually</option>
                                                    <option value="List">Pick value from list</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div class="col-sm-4" v-if="allowMultipleValuesEnabled === true && inputType !== 'Evaluate'">
                                            <div class="form-group">
                                                <label>Multi-select</label>
                                                <div class="checkbox">
                                                    <label>
                                                        <input v-model="isMultivalue" type="checkbox"> Allow multiple values
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                <br />

                                <template v-if="inputType === 'Manual'">
                                    <div v-if="attributeType === 'Text'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min length</label>
                                            <input v-model.number="minLength" type="number" class="form-control" min="0" step="1" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max length</label>
                                            <input v-model.number="maxLength" type="number" class="form-control" min="1" step="1" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'MultilineText'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min length</label>
                                            <input v-model.number="minLength" type="number" class="form-control" min="0" step="1" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max length</label>
                                            <input v-model.number="maxLength" type="number" class="form-control" min="1" step="1">
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Number of lines</label>
                                            <input v-model.number="rows" type="number" class="form-control" min="0" step="1" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'Real'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min value</label>
                                            <input v-model.number="minValue" type="number" class="form-control" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max value</label>
                                            <input v-model.number="maxValue" type="number" class="form-control" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'Date'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min value</label>
                                            <DatePicker :value="minDate" @change="onMinDateChanged" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max value</label>
                                            <DatePicker :value="maxDate" @change="onMaxDateChanged" />
                                        </div>
                                    </div>

                                </template>

                                <template v-if="inputType === 'List'">
                                    <div class="mt-3">
                                        <button class="btn btn-sm btn-outline-secondary mr-1" @click="addDefinedValue">Add value</button>

                                        <div v-if="attributeType === 'Real'" class="dropdown d-inline">
                                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                                Scales templates
                                            </button>

                                            <div class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                                                <a v-for="s in scales" :key="s.Id" class="dropdown-item" @click="definedValues = s.Levels">
                                                    {{s.Name}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row mt-3">
                                        <div class="col-sm-3">
                                            <label>Name</label>
                                        </div>
                                        <div class="col-sm-4">
                                            <label>
                                                {{ attributeType !== 'Email' ? 'Value' : 'Email'  }}
                                            </label>
                                        </div>
                                        <div class="col-sm-4">
                                            <label>Description</label>
                                        </div>
                                    </div>

                                    <div v-for="dv in definedValues">
                                        <div class="row" style="margin-top: 5px;">
                                            <div class="col-sm-3">
                                                <input type="text" v-model.trim="dv.Name" class="form-control" required minlength="2" maxlength="100" />
                                            </div>
                                            <div class="col-sm-4">
                                                <div v-if="attributeType === 'Text'">
                                                    <input type="text" v-model.trim="dv.Value" required class="form-control" />
                                                </div>

                                                <div v-if="attributeType === 'Real'">
                                                    <input type="number" v-model.number="dv.Value" required class="form-control" />
                                                </div>

                                                <div v-if="attributeType === 'Date'">
                                                    <DatePicker :value="dv.Value != null ? new Date(dv.Value) : null" @change="onDefinedLevelDateChanged($event, dv)" />
                                                </div>

                                                <div v-if="attributeType === 'Email'">
                                                    <input type="email" v-model.trim="dv.Value" required class="form-control" />
                                                </div>
                                            </div>

                                            <div class="col-sm-4">
                                                <textarea type="text" v-model.trim="dv.Description" class="form-control" rows="2"></textarea>
                                            </div>

                                            <div class="col-sm-1">
                                                <button type="button" @click="removeDefinedValue(dv)" class="btn btn-outline-secondary">
                                                    <i class="fa fa-times-circle"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-for="e in definedValuesErrors">
                                        <div class="error">{{e}}</div>
                                    </div>

                                </template>

                                <template v-if="inputType === 'Evaluate'">
                                    <label>Formula</label>
                                    <div class="d-flex justify-content-end">
                                        <div class="dropdown">
                                            <button class="btn btn-sm btn-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                            <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                <a v-for="attrName in numericAttributeNames" :key="attrName" class="dropdown-item" @click="insertAttributeIntoScript(attrName)">
                                                    {{attrName}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <textarea ref="ta" v-model="script" rows="5" class="form-control"></textarea>
                                        <div class="error">{{scriptError}}</div>
                                    </div>

                                    <a class="text-primary" @click="testScriptPanelVisible = !testScriptPanelVisible">Test formula</a>
                                    <br />
                                    <div v-if="testScriptPanelVisible === true">
                                        <div class="shadow-none p-3 mb-3 bg-light rounded">
                                            <div v-for="i in scriptTestInputs" class="form-inline">
                                                <label>{{i.Name}}: </label>
                                                <input type="number" v-model.number="i.Value" class="form-control" style="width: 90px;" />
                                            </div>
                                            <br />

                                            <div class="row">
                                                <div class="col-sm-6">
                                                    <button type="button" @click="handleLoadScriptTestResult" class="btn btn-outline-secondary">Test</button>
                                                </div>

                                                <div class="col-sm-6">
                                                    <h3 style="margin: 0;">
                                                        Result: {{scriptTestResult}}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </form>

                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="submit" form="attribute-editor-dialog-form" :disabled="!initialized" class="btn btn-primary">{{confirmButtonLabel}}</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .k-calendar-container {
        z-index: 10000 !important;
    }
</style>